import { useCallback, useEffect, useRef, useState } from 'react';

const CONTRIBUTORS_REFRESH_INTERVAL_MS = 5_000;

type RefreshOptions = {
	queueIfInFlight?: boolean;
};

type ContributorsPollingResult = {
	error?: Error;
	refreshNow: (options?: RefreshOptions) => Promise<void>;
};

// Refresh only while this tab is visible, keeping the last result on screen between requests.
export const useContributorsPolling = (
	refresh: () => Promise<unknown>,
	{ pollingEnabled }: { pollingEnabled: boolean },
): ContributorsPollingResult => {
	const [error, setError] = useState<Error>();
	const refreshRef = useRef<ContributorsPollingResult['refreshNow'] | undefined>(undefined);
	const refreshNow = useCallback(
		(options?: RefreshOptions) => refreshRef.current?.(options) ?? Promise.resolve(),
		[],
	);

	useEffect(() => {
		setError(undefined);

		let disposed = false;
		let inFlight = false;
		let refreshQueued = false;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const clearTimer = () => {
			if (timer !== undefined) {
				clearTimeout(timer);
				timer = undefined;
			}
		};

		const scheduleRefresh = () => {
			if (pollingEnabled && !disposed && !document.hidden) {
				timer = setTimeout(refreshContributors, CONTRIBUTORS_REFRESH_INTERVAL_MS);
			}
		};

		const refreshContributors = async ({ queueIfInFlight = false }: RefreshOptions = {}) => {
			// Opening the dropdown replaces the pending poll; reuse any request already in flight.
			clearTimer();
			if (disposed || document.hidden) {
				return;
			}
			if (inFlight) {
				// A publish may arrive after the active request read the previous version.
				refreshQueued = refreshQueued || queueIfInFlight;
				return;
			}

			inFlight = true;
			try {
				do {
					refreshQueued = false;
					try {
						await refresh();
						if (!disposed) {
							setError(undefined);
						}
					} catch (error) {
						if (!disposed) {
							setError(
								error instanceof Error ? error : new Error('Failed to refresh contributors'),
							);
						}
					}
				} while (refreshQueued && !disposed && !document.hidden);
			} finally {
				inFlight = false;
				// Live docs restart their polling interval after any request finishes.
				scheduleRefresh();
			}
		};

		const onVisibilityChange = () => {
			clearTimer();
			if (!document.hidden) {
				// Without polling, no later timer will catch changes missed while hidden.
				void refreshContributors({ queueIfInFlight: !pollingEnabled });
			}
		};

		// Manual refreshes use the same timer and in-flight guard as scheduled requests.
		refreshRef.current = refreshContributors;
		document.addEventListener('visibilitychange', onVisibilityChange);
		// The query reader handles initial data; wait for the first poll instead of fetching twice.
		scheduleRefresh();

		return () => {
			disposed = true;
			refreshRef.current = undefined;
			clearTimer();
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [refresh, pollingEnabled]);

	return { error, refreshNow };
};
